/**
 * Índice e quadros do rastreio de nuvens publicados pela máquina do agendador
 * (container `pyfortracc_ir`, no repositório do pyForTraCC).
 *
 * A cada 10 min o pyForTraCC rastreia os sistemas de nuvens frias do canal 13
 * (infravermelho) do GOES-19 e prevê onde cada um estará nas 3 h seguintes —
 * por persistência do deslocamento ou, desde 23/10/2026, por uma rede recorrente
 * (GRU) treinada fora da máquina (`previsao.metodo`) —, de 30 em 30 min (até 29/09/2026 eram 60 min,
 * de 10 em 10). O passo e o número de passos vêm do índice (`previsao.passos` e
 * `previsao.passo_min`): nada aqui os fixa. A máquina publica dois GeoJSON por
 * horário, com CORS `*`:
 * - `quadros/{c}.geojson` — contornos observados (`k: "c"`) e trajetórias (`k: "t"`);
 * - `previsao/{c}.geojson` — contornos previstos a partir de `c` (`k: "p"`),
 *   com `h` = antecedência em minutos: o contorno observado transladado pelo
 *   vetor médio do sistema vezes o número de passos (persistência), ou movido
 *   pelo deslocamento e dilatado/erodido para a área que a rede prevê.
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { parseStarStamp } from '../goes/stamp';

/** Onde a máquina publica. `VITE_FORTRACC_URL` aponta para outro lugar (vazio desliga a camada). */
export const BASE_RASTREIO = (import.meta.env.VITE_FORTRACC_URL ?? 'https://147.15.84.134/fortracc').replace(/\/$/, '');

/**
 * Versão do formato. Os quadros têm cache imutável no navegador: a `v2` trocou a
 * previsão (o contorno transladado pelo vetor do sistema, com precisão de
 * subpixel; na `v1` os sistemas lentos não saíam do lugar). A `v3` acrescentou a
 * cada contorno observado a história do sistema (`hist`) e `tmax`, `dp`, `exp`
 * e `nuc` — ver docs/rastreio.md.
 */
export const VERSAO = 'v3';

export const PYFORTRACC_URL = 'https://github.com/fortracc/pyfortracc';

export const RASTREIO_ATRIBUICAO = 'Rastreio de nuvens: pyForTraCC — GOES-19 ABI (canal 13)';

/** Antecedências quando o índice não as informa: 6 passos de 30 min, o que a máquina publica desde 29/09/2026. */
export const ANTECEDENCIAS_PADRAO: readonly number[] = [30, 60, 90, 120, 150, 180];

export type TipoQuadro = 'quadros' | 'previsao';

export interface QuadroRastreio {
  /** Início da varredura (epoch ms, UTC) — o mesmo horário do quadro do STAR. */
  t: number;
  /** Carimbo AAAADDDHHMM (nome do arquivo). */
  c: string;
  /** Sistemas no primeiro limiar. */
  n: number;
  /** Área somada dos sistemas no primeiro limiar (km²). */
  km2: number;
  /** Temperatura de brilho mais fria do quadro (K), ou null. */
  tmin: number | null;
  /** A previsão a partir deste horário existe. */
  prev: boolean;
}

export interface IndiceRastreio {
  /** Quando a máquina escreveu o índice (epoch ms), ou null. */
  gerado: number | null;
  /** Limiares de temperatura de brilho (K), do sistema para o núcleo. */
  limiares: number[];
  /** Antecedências da previsão (min), crescentes. */
  antecedencias: number[];
  /**
   * Como a máquina prevê agora (`previsao.metodo`): `persistencia` (o contorno
   * transladado pelo vetor médio, mesma forma e tamanho) ou `rede` (rede
   * recorrente do pyForTraCC, `gru`/`lstm`: vetor, área e intensidade previstos).
   */
  metodo?: MetodoPrevisao;
  /** Quadros em ordem crescente de tempo. */
  quadros: QuadroRastreio[];
}

export type MetodoPrevisao = 'persistencia' | 'rede';

const numero = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Interpreta o índice; entradas inválidas são descartadas. Lança erro só quando o arquivo inteiro não serve. */
export function lerIndice(cru: unknown): IndiceRastreio {
  const m = cru as {
    gerado?: unknown;
    quadros?: unknown;
    limiares_K?: unknown;
    previsao?: { passos?: unknown; passo_min?: unknown; metodo?: unknown };
  };
  if (!m || typeof m !== 'object' || !Array.isArray(m.quadros)) throw new Error('índice do rastreio sem a lista `quadros`');
  const vistos = new Set<number>();
  const quadros: QuadroRastreio[] = [];
  for (const q of m.quadros as Record<string, unknown>[]) {
    if (!q || typeof q.c !== 'string' || !/^\d{11}$/.test(q.c)) continue;
    const t = parseStarStamp(q.c);
    if (t === null || vistos.has(t)) continue;
    vistos.add(t);
    quadros.push({
      t,
      c: q.c,
      n: Math.max(0, Math.round(numero(q.n) ?? 0)),
      km2: Math.max(0, numero(q.km2) ?? 0),
      tmin: numero(q.tmin),
      prev: q.prev === true,
    });
  }
  quadros.sort((a, b) => a.t - b.t);
  const gerado = typeof m.gerado === 'string' ? Date.parse(m.gerado) : NaN;
  const limiares = Array.isArray(m.limiares_K) ? m.limiares_K.filter((v): v is number => numero(v) !== null) : [];
  const passos = numero(m.previsao?.passos);
  const passo = numero(m.previsao?.passo_min);
  const antecedencias =
    passos !== null && passo !== null && passos >= 1 && passos <= 36 && passo > 0
      ? Array.from({ length: Math.round(passos) }, (_, i) => (i + 1) * passo)
      : [...ANTECEDENCIAS_PADRAO];
  const metodo: MetodoPrevisao = m.previsao?.metodo === 'gru' || m.previsao?.metodo === 'lstm' ? 'rede' : 'persistencia';
  return { gerado: Number.isFinite(gerado) ? gerado : null, limiares, antecedencias, metodo, quadros };
}

/** O índice muda a cada quadro novo; `no-cache` revalida no servidor (ele responde 304 quando nada mudou). */
export async function carregarIndice(signal?: AbortSignal): Promise<IndiceRastreio> {
  const cru = await fetchWithRetry<unknown>(`${BASE_RASTREIO}/${VERSAO}/indice.json`, { cache: 'no-cache', mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
  return lerIndice(cru);
}

export function urlQuadro(tipo: TipoQuadro, c: string, base: string = BASE_RASTREIO): string {
  return `${base}/${VERSAO}/${tipo}/${c}.geojson`;
}

/** Baixa um quadro (imutável: o cache HTTP do navegador guarda). */
export async function carregarQuadro(tipo: TipoQuadro, c: string, signal?: AbortSignal): Promise<unknown> {
  return fetchWithRetry<unknown>(urlQuadro(tipo, c), { mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
}
